import React, { useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { DOCUMENT, documentOutlineButtonSx, documentTextButtonSx } from '../constants/documentTheme';
import PartnerInventoryPickerDialog from './PartnerInventoryPickerDialog';
import PartnerCatalogItemDialog from './PartnerCatalogItemDialog';
import PartnerTripDeliveryOptions from './PartnerTripDeliveryOptions';
import { CATALOG_CONFIGS } from '../utils/partnerCatalog';

const PARTNER_AUTH = { usePartnerAuth: true };

/**
 * Creates or picks an add-on for one traveler on a group trip. It is stored on
 * that traveler's trip only. Shared add-ons stay on the group Add-ons tab.
 */
const PartnerPersonalAddOnButton = ({ travelerTripId, travelerName }) => {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [itemId, setItemId] = useState('');
  const [offering, setOffering] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [pricingMode, setPricingMode] = useState('charge');
  const [deliveryMode, setDeliveryMode] = useState('offer');

  const resetDelivery = () => {
    setQuantity('1');
    setPricingMode('charge');
    setDeliveryMode('offer');
  };

  const offerItem = async (catalogItemId) => {
    const free = pricingMode === 'free';
    await api.post(
      `${CATALOG_CONFIGS.addon.endpoint}/items/${catalogItemId}/offer-on-trip`,
      {
        tripId: Number(travelerTripId),
        audience: 'personal',
        free,
        quantity: free ? 1 : Math.max(1, Number(quantity) || 1),
        mode: deliveryMode,
      },
      PARTNER_AUTH
    );
    setNotice(t('itinerary.personalAddonAdded', { name: travelerName }));
  };

  const openCreate = () => {
    setError('');
    setNotice('');
    resetDelivery();
    setCreateOpen(true);
  };

  const openPicker = async () => {
    try {
      setError('');
      setNotice('');
      const response = await api.get(`${CATALOG_CONFIGS.addon.endpoint}/items`, PARTNER_AUTH);
      setItems(response.data.items || []);
      setItemId('');
      resetDelivery();
      setPickerOpen(true);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerAddons.offerFailed'));
    }
  };

  const offerPicked = async () => {
    try {
      setOffering(true);
      setError('');
      await offerItem(itemId);
      setPickerOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerAddons.offerFailed'));
    } finally {
      setOffering(false);
    }
  };

  const addCreatedAddon = async (addon) => {
    setCreateOpen(false);
    try {
      setError('');
      await offerItem(addon.id);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerAddons.createdNotAdded', { name: addon.name }));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', sm: 'flex-end' }, gap: 0.5 }}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {notice ? <Alert severity="success">{notice}</Alert> : null}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Button
          onClick={openCreate}
          sx={documentOutlineButtonSx}
          data-testid="personal-addon-create"
        >
          {t('itinerary.addPersonalAddon', { name: travelerName })}
        </Button>
        <Button
          onClick={openPicker}
          sx={documentTextButtonSx}
          data-testid="personal-addon-existing"
        >
          {t('partnerAddons.fromAddons')}
        </Button>
      </Box>
      <PartnerCatalogItemDialog
        open={createOpen}
        kind="addon"
        onClose={() => setCreateOpen(false)}
        onSaved={addCreatedAddon}
        title={t('itinerary.addPersonalAddon', { name: travelerName })}
        saveLabel={t('partnerAddons.createAndAdd')}
        renderExtra={(form) => (
          <>
            <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>
              {t('itinerary.personalAddonCreateHint', { name: travelerName })}
            </Typography>
            <PartnerTripDeliveryOptions
              item={form.name.trim() ? form : null}
              pricingMode={pricingMode}
              onPricingModeChange={setPricingMode}
              deliveryMode={deliveryMode}
              onDeliveryModeChange={setDeliveryMode}
              showQuantity={form.priceFrequency !== 'one_time'}
              quantity={quantity}
              onQuantityChange={setQuantity}
              testIdPrefix="personal-addon-new"
            />
          </>
        )}
      />
      <PartnerInventoryPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        items={items}
        selectedId={itemId}
        onSelect={setItemId}
        onConfirm={offerPicked}
        confirming={offering}
        title={t('partnerAddons.fromAddons')}
        confirmLabel={t('partnerAddons.offerAddon')}
        emptyLabel={t('partnerAddons.addonsEmpty')}
        showQuantity
        quantity={quantity}
        onQuantityChange={setQuantity}
        pricingMode={pricingMode}
        onPricingModeChange={setPricingMode}
        deliveryMode={deliveryMode}
        onDeliveryModeChange={setDeliveryMode}
        categoryKeyPrefix="partnerAddonCatalog.category"
        testIdPrefix="personal-addon"
      />
    </Box>
  );
};

export default PartnerPersonalAddOnButton;
